import { useMemo } from "react";
import { useAnnualQuery } from "@services/domains/packages/package-annual-schedule";
import type { Interval } from "@models/ISchema";
import { getCellOccurrences } from "../../adapters";
import type { AnnualScheduleCellSelection, AnnualScheduleContext } from "../../types";

/**
 * Fetches a league and prepares occurrences for the selected cell.
 *
 * @param context - Scenario and origin in which the league is queried.
 * @param selection - Selected league, component, and week IDs, if available.
 * @param week - Selected week, if available, used to calculate occurrence dates.
 * @param scopeKey - Identity of the package context containing the cell.
 * @param enabled - Requests detail only while the drawer has a complete open selection.
 * @returns Cell occurrences, query state, and a refresh function.
 *
 * @remarks The league calendar includes all weeks; filtering is applied locally. The hook
 * may remain mounted while the drawer is closed, so incomplete selections never query.
 */
export const usePackageAnnualCell = (
  context: AnnualScheduleContext,
  selection: AnnualScheduleCellSelection | null | undefined,
  week: Interval | undefined,
  scopeKey: string,
  enabled = true,
) => {
  const result = useAnnualQuery(
    {
      scenarioId: context.scenarioId,
      originId: context.originId,
      resourceId: selection?.leagueId ?? "",
    },
    JSON.stringify([scopeKey, selection?.leagueId, selection?.componentId, selection?.intervalId]),
    enabled && !!selection && !!week,
  );

  const occurrences = useMemo(
    () =>
      selection && week
        ? getCellOccurrences(
            result.data?.cube?.editorView?.sections ?? [],
            selection.componentId,
            week,
          )
        : [],
    [result.data, selection?.componentId, week],
  );

  return { ...result, occurrences };
};
